import React, { useEffect, useState, useRef } from "react";
import "./DashboardHome.scss";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Card, CardContent, Typography, LinearProgress, Tooltip } from "@mui/material";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBatteryFull,
  faThermometerHalf,
  faBolt,
  faPlug,
  faHouse,
  faCircleCheck,
  faTriangleExclamation,
  faCircleQuestion,
  faHeartPulse,
} from "@fortawesome/free-solid-svg-icons";
import { isSubThresholdOutputWatt } from "../../utils/telemetry";

const API_BASE = "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod";

const createEmptySparklineData = () => ({
  gridVoltage: [],
  generatorVoltage: [],
  outputVoltage: [],
  batteryVoltage: [],
});

const createDefaultScale = () => ({ min: 0, max: 1, seeded: false });

const createEmptySparklineScales = () => ({
  gridVoltage: createDefaultScale(),
  generatorVoltage: createDefaultScale(),
  outputVoltage: createDefaultScale(),
  batteryVoltage: createDefaultScale(),
});

const getPaddedScale = (min, max) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return createDefaultScale();
  }
  const span = Math.abs(max - min);
  const padding = span === 0 ? Math.max(Math.abs(max) * 0.05, 0.5) : span * 0.1;
  return {
    min: min - padding,
    max: max + padding,
    seeded: true,
  };
};

const DashboardHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeProductId } = useParams();

  const locationProduct = location.state?.product || null;
  const [product, setProduct] = useState(() => {
    if (locationProduct) {
      return locationProduct;
    }
    if (routeProductId) {
      const cachedProduct = sessionStorage.getItem(`dashboardProduct:${routeProductId}`);
      return cachedProduct ? JSON.parse(cachedProduct) : null;
    }
    return null;
  });

  const productId = product?.id || routeProductId;

  const [, setLogData] = useState([]);
  const [, setLogId] = useState(0);
  const logDataRef = useRef([]);
  const [fetchStatus, setFetchStatus] = useState("");
  const [, setTelemetryFreshness] = useState(null);
  const [dashboardData, setDashboardData] = useState({});
  const [progress, setProgress] = useState(0);
  const [lastEvent, setLastEvent] = useState({ message: "No Events", timestamp: null });
  const [sparklineData, setSparklineData] = useState({
    gridVoltage: [],
    generatorVoltage: [],
    outputVoltage: [],
    batteryVoltage: [],
  });
  const [sparklineScales, setSparklineScales] = useState(() => createEmptySparklineScales());
  const [highlightedFields, setHighlightedFields] = useState({});
  const [firmwareVersion, setFirmwareVersion] = useState(null);
  const [isFirmwareVersionLoading, setIsFirmwareVersionLoading] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [isHealthLoading, setIsHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");
  const [nowTs, setNowTs] = useState(() => Date.now());

  const intervalRef = useRef(null);
  const highlightTimeouts = useRef({});
  const processingFileRef = useRef(null);
  const telemetryCursorRef = useRef(null);
  const sparklineScalesRef = useRef(sparklineScales);
  const sparklineHistoryRef = useRef([]);
  const HIGHLIGHT_DURATION = 1500; // ms

  const ESTIMATED_SAMPLE_RATE = 8; // samples per second observed in logs
  const TARGET_BUFFER_SECONDS = 30;
  const FAST_FORWARD_THRESHOLD_SECONDS = 90;
  const MAX_BUFFER_SECONDS = 150;
  const MAX_DEQUEUE_PER_TICK = 5;
  const CURRENT_WINDOW_SECONDS = 300;
  const FETCH_INTERVAL = 30 * 1000; // Poll every 30 seconds to align with 90-second ingest
  const DATA_BUFFER_SIZE = ESTIMATED_SAMPLE_RATE * MAX_BUFFER_SECONDS;
  const TARGET_BUFFER_SAMPLES = ESTIMATED_SAMPLE_RATE * TARGET_BUFFER_SECONDS;
  const FAST_FORWARD_THRESHOLD_SAMPLES =
    ESTIMATED_SAMPLE_RATE * FAST_FORWARD_THRESHOLD_SECONDS;
  const SPARKLINE_RETENTION_SECONDS = 10 * 60; // 10 minutes of telemetry
  const SPARKLINE_RETENTION_MS = SPARKLINE_RETENTION_SECONDS * 1000;
  const SPARKLINE_TARGET_POINTS = 120;
  const SPARKLINE_BUCKET_MS = Math.max(
    1000,
    Math.floor(SPARKLINE_RETENTION_MS / SPARKLINE_TARGET_POINTS)
  );

  const modeValues = {
    0: "Standby/Fault Mode",
    1: "Inverter Mode",
    2: "Charger Mode",
    3: "Power Saving Mode",
  };

  const chargeStageValues = {
    0: "standby",
    1: "bulk current",
    2: "absorb voltage",
    3: "equalization voltage",
    4: "floating voltage",
    5: "charging completed",
  };

  const formatValue = (value, fractionDigits = 1) => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
      return "--";
    }
    if (numericValue > 0 && numericValue < 0.1) {
      return "<0.1";
    }
    return numericValue.toFixed(fractionDigits);
  };

  const TWO_DECIMAL_KEYS = new Set([
    "output_volt",
    "output_freq",
    "output_current",
    "output_va",
    "output_watt",
    "input_watt",
  ]);

  const formatMetricValue = (key, rawValue) => {
    if (key === "charge_stage") {
      const stageIndex = Number(rawValue);
      return chargeStageValues[Number.isNaN(stageIndex) ? undefined : stageIndex] || "--";
    }
    const decimals = TWO_DECIMAL_KEYS.has(key) ? 2 : 1;
    return formatValue(rawValue, decimals);
  };

  const formatPowerThresholdValue = (key, rawValue, unit) => {
    const numericValue = parseFloat(rawValue);
    if (Number.isNaN(numericValue)) {
      return `--${unit}`;
    }
    const isBelowThreshold = key === "output_watt"
      ? isSubThresholdOutputWatt(numericValue)
      : numericValue >= 0 && numericValue < 12;
    if (isBelowThreshold) {
      return `<12${unit}`;
    }
    return `${formatMetricValue(key, rawValue)}${unit}`;
  };

  const metricKeys = [
    "grid_volt",
    "grid_freq",
    "input_current",
    "input_watt",
    "input_va",
    "gen_volt",
    "gen_freq",
    "output_volt",
    "output_freq",
    "output_current",
    "output_va",
    "output_watt",
    "battery_volt",
    "battery_current",
    "external_current",
    "charge_stage",
    "battery_temp",
    "heatsink_temp",
    "transformer_temp",
  ];

  const getComparableMetricValue = (key, rawValue) => {
    return formatMetricValue(key, rawValue);
  };

  const formatPowerFactor = (wattsRaw, vaRaw) => {
    const watts = parseFloat(wattsRaw);
    const va = parseFloat(vaRaw);
    if (Number.isNaN(watts) || Number.isNaN(va) || va === 0) {
      return "0.000";
    }
    return Math.min(1, Math.abs(watts / va)).toFixed(3);
  };

  useEffect(() => {
    const timerId = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (locationProduct) {
      setProduct(locationProduct);
      sessionStorage.setItem(
        `dashboardProduct:${locationProduct.id}`,
        JSON.stringify(locationProduct)
      );
    }
  }, [locationProduct]);

  useEffect(() => {
    if (!product && routeProductId) {
      const cached = sessionStorage.getItem(`dashboardProduct:${routeProductId}`);
      if (cached) {
        setProduct(JSON.parse(cached));
      }
    }
  }, [product, routeProductId]);

  useEffect(() => {
    if (!productId) {
      setFetchStatus("Select a product to view telemetry");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [productId]);

  useEffect(() => {
    telemetryCursorRef.current = null;
    logDataRef.current = [];
    setLogData([]);
    setLogId(0);
    setDashboardData({});
    setFetchStatus("");
    setTelemetryFreshness(null);
    setProgress(0);
    setLastEvent({ message: "No Events", timestamp: null });
    sparklineHistoryRef.current = [];
    setSparklineData(createEmptySparklineData());
    const resetScales = createEmptySparklineScales();
    sparklineScalesRef.current = resetScales;
    setSparklineScales(resetScales);
  }, [productId]);

  useEffect(() => {
    if (!productId) {
      setFirmwareVersion(null);
      setIsFirmwareVersionLoading(false);
      return;
    }

    let isSubscribed = true;

    const fetchFirmwareVersion = async () => {
      setIsFirmwareVersionLoading(true);
      try {
        const response = await fetch(`${API_BASE}/logs/get/version/${productId}`);
        const body = await response.json();

        if (!isSubscribed) {
          return;
        }

        if (response.ok && body?.data?.firmwareVersion !== undefined) {
          const numericValue = Number(body.data.firmwareVersion);
          setFirmwareVersion(
            Number.isNaN(numericValue) ? null : (numericValue / 100).toFixed(2)
          );
        } else {
          console.warn("Firmware version not available for product", productId);
          setFirmwareVersion(null);
        }
      } catch (error) {
        if (isSubscribed) {
          console.error("Error fetching firmware version:", error);
          setFirmwareVersion(null);
        }
      } finally {
        if (isSubscribed) {
          setIsFirmwareVersionLoading(false);
        }
      }
    };

    fetchFirmwareVersion();

    return () => {
      isSubscribed = false;
    };
  }, [productId]);

  useEffect(() => {
    if (!productId) {
      setHealthStatus(null);
      setHealthError("");
      setIsHealthLoading(false);
      return undefined;
    }

    let isSubscribed = true;

    const fetchHealthStatus = async () => {
      setIsHealthLoading(true);
      try {
        const response = await fetch(`${API_BASE}/logs/get/health/${productId}`);
        const body = await response.json();

        if (!isSubscribed) {
          return;
        }

        if (response.ok && body?.status === "ok") {
          setHealthStatus(body.data || null);
          setHealthError("");
        } else {
          setHealthStatus(null);
          setHealthError("Health unavailable");
          console.warn("Health status not available for product", productId, body);
        }
      } catch (error) {
        if (isSubscribed) {
          setHealthStatus(null);
          setHealthError("Health unavailable");
          console.error("Error fetching health status:", error);
        }
      } finally {
        if (isSubscribed) {
          setIsHealthLoading(false);
        }
      }
    };

    fetchHealthStatus();
    const timerId = setInterval(fetchHealthStatus, FETCH_INTERVAL);

    return () => {
      isSubscribed = false;
      clearInterval(timerId);
    };
  }, [productId, FETCH_INTERVAL]);

  const handleNavigateHome = () => {
    navigate("/product");
  };

  const flagUpdatedFields = (keys = []) => {
    if (!keys.length) {
      return;
    }

    setHighlightedFields((prev) => {
      const next = { ...prev };

      keys.forEach((key) => {
        next[key] = true;

        if (highlightTimeouts.current[key]) {
          clearTimeout(highlightTimeouts.current[key]);
        }

        highlightTimeouts.current[key] = setTimeout(() => {
          setHighlightedFields((current) => {
            const updated = { ...current };
            delete updated[key];
            return updated;
          });
          delete highlightTimeouts.current[key];
        }, HIGHLIGHT_DURATION);
      });

      return next;
    });
  };

  const toSparklineNumber = (value) => {
    const numericValue = parseFloat(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const rebuildSparklineSeries = () => {
    const history = sparklineHistoryRef.current;
    if (!history.length) {
      const emptyData = createEmptySparklineData();
      setSparklineData(emptyData);
      const resetScales = createEmptySparklineScales();
      sparklineScalesRef.current = resetScales;
      setSparklineScales(resetScales);
      return;
    }

    const latestTimestamp = history[history.length - 1].timestamp;
    const endTime = Math.max(Date.now(), latestTimestamp);
    const startTime = endTime - SPARKLINE_RETENTION_MS;

    const bucketData = createEmptySparklineData();
    let historyIndex = 0;
    let lastPoint = null;

    for (let bucket = 0; bucket < SPARKLINE_TARGET_POINTS; bucket += 1) {
      const bucketEnd = startTime + (bucket + 1) * SPARKLINE_BUCKET_MS;
      while (historyIndex < history.length && history[historyIndex].timestamp <= bucketEnd) {
        lastPoint = history[historyIndex];
        historyIndex += 1;
      }

      bucketData.gridVoltage.push(lastPoint ? lastPoint.gridVoltage : null);
      bucketData.generatorVoltage.push(lastPoint ? lastPoint.generatorVoltage : null);
      bucketData.outputVoltage.push(lastPoint ? lastPoint.outputVoltage : null);
      bucketData.batteryVoltage.push(lastPoint ? lastPoint.batteryVoltage : null);
    }

    const deriveScaleForKey = (key) => {
      const values = bucketData[key].filter((value) => typeof value === "number");
      if (!values.length) {
        return sparklineScalesRef.current[key] || createDefaultScale();
      }

      const currentMin = Math.min(...values);
      const currentMax = Math.max(...values);
      const previousScale = sparklineScalesRef.current[key];
      const shouldAnchor = Boolean(previousScale?.seeded);

      const anchoredMin = shouldAnchor ? Math.min(previousScale.min, currentMin) : currentMin;
      const anchoredMax = shouldAnchor ? Math.max(previousScale.max, currentMax) : currentMax;

      return getPaddedScale(anchoredMin, anchoredMax);
    };

    const nextScales = {
      gridVoltage: deriveScaleForKey("gridVoltage"),
      generatorVoltage: deriveScaleForKey("generatorVoltage"),
      outputVoltage: deriveScaleForKey("outputVoltage"),
      batteryVoltage: deriveScaleForKey("batteryVoltage"),
    };

    sparklineScalesRef.current = nextScales;
    setSparklineScales(nextScales);
    setSparklineData(bucketData);
  };

  const appendSparklineSample = (sample) => {
    const timestampCandidate =
      sample.date && sample.time ? Date.parse(`${sample.date}T${sample.time}Z`) : Date.now();
    const timestamp = Number.isNaN(timestampCandidate) ? Date.now() : timestampCandidate;

    const history = sparklineHistoryRef.current;
    history.push({
      timestamp,
      gridVoltage: toSparklineNumber(sample.grid_volt),
      generatorVoltage: toSparklineNumber(sample.gen_volt),
      outputVoltage: toSparklineNumber(sample.output_volt),
      batteryVoltage: toSparklineNumber(sample.battery_volt),
    });

    const cutoff = timestamp - SPARKLINE_RETENTION_MS;
    while (history.length && history[0].timestamp < cutoff) {
      history.shift();
    }

    rebuildSparklineSeries();
  };

  const formatProcessingLabel = (name, percent) => `Processing ${name} (${percent}%)`;

  const clearProcessingStatus = () => {
    processingFileRef.current = null;
    setFetchStatus("");
  };

  const markSamplesProcessed = (sampleLogId, count = 1) => {
    const info = processingFileRef.current;
    if (!info || sampleLogId !== info.id) {
      return;
    }

    info.processed = Math.min(info.total, info.processed + count);

    if (info.processed >= info.total) {
      clearProcessingStatus();
      return;
    }

    const percent = info.total > 0 ? Math.round((info.processed / info.total) * 100) : 0;
    setFetchStatus(formatProcessingLabel(info.name, percent));
  };

  const getBufferedSeconds = () => logDataRef.current.length / ESTIMATED_SAMPLE_RATE;

  const updateProgressFromBacklog = () => {
    const backlogSeconds = getBufferedSeconds();

    if (backlogSeconds <= 0) {
      setProgress(100);
      return;
    }

    if (backlogSeconds >= FAST_FORWARD_THRESHOLD_SECONDS) {
      setProgress(0);
      return;
    }

    const normalized =
      ((FAST_FORWARD_THRESHOLD_SECONDS - backlogSeconds) / FAST_FORWARD_THRESHOLD_SECONDS) * 100;
    setProgress(Math.max(0, Math.min(normalized, 100)));
  };

  const maybeFastForwardQueue = () => {
    if (logDataRef.current.length <= FAST_FORWARD_THRESHOLD_SAMPLES) {
      return;
    }

    const samplesToKeep = Math.max(TARGET_BUFFER_SAMPLES, ESTIMATED_SAMPLE_RATE * 10);
    if (logDataRef.current.length <= samplesToKeep) {
      return;
    }

    const samplesToDrop = logDataRef.current.length - samplesToKeep;
    const droppedEntries = logDataRef.current.slice(0, samplesToDrop);
    if (processingFileRef.current && samplesToDrop > 0) {
      const droppedForCurrent = droppedEntries.filter(
        (entry) => entry.log_id === processingFileRef.current.id
      ).length;
      if (droppedForCurrent > 0) {
        markSamplesProcessed(processingFileRef.current.id, droppedForCurrent);
      }
    }
    logDataRef.current = logDataRef.current.slice(-samplesToKeep);
    updateProgressFromBacklog();
  };

  const updateTelemetryCursor = (sample) => {
    if (!sample) {
      return;
    }

    const nextCursor = sample.cursor || {
      sampleId: sample.id,
      logId: sample.log_id,
      sampleTimestamp: sample.sampleTimestamp,
    };
    const nextSampleId = Number(nextCursor.sampleId);
    const currentSampleId = Number(telemetryCursorRef.current?.sampleId);

    if (!Number.isFinite(nextSampleId)) {
      return;
    }

    if (!Number.isFinite(currentSampleId) || nextSampleId >= currentSampleId) {
      telemetryCursorRef.current = nextCursor;
    }
  };

  const applyTelemetrySample = (sample, highlightChanges = true) => {
    if (!sample) {
      return;
    }

    setDashboardData((prevDashboardData) => {
      const previous = prevDashboardData || {};
      if (highlightChanges) {
        const changedKeys = metricKeys.filter((key) => {
          const nextDisplay = getComparableMetricValue(key, sample[key]);
          const prevDisplay = getComparableMetricValue(key, previous[key]);
          return nextDisplay !== prevDisplay;
        });
        flagUpdatedFields(changedKeys);
      }
      return sample;
    });

    setLastEvent((prevEvent) => {
      if (sample.event && sample.event !== prevEvent.message) {
        return { message: sample.event, timestamp: new Date() };
      }
      return prevEvent;
    });

    appendSparklineSample(sample);
    updateTelemetryCursor(sample);
  };

  const processNextDataPoint = () => {
    if (!logDataRef.current.length) {
      updateProgressFromBacklog();
      return;
    }

    const currentData = logDataRef.current.shift();
    if (!currentData) {
      return;
    }

    applyTelemetrySample(currentData);
    markSamplesProcessed(currentData.log_id);
    updateProgressFromBacklog();
  };

  const buildTelemetryStatusText = (freshness) => {
    if (!freshness) {
      return "";
    }

    if (freshness.freshnessStatus === "stale") {
      return `Stale telemetry: ${formatRelativeAge(freshness.ageSeconds)}`;
    }

    if (freshness.freshnessStatus === "empty") {
      return freshness.freshnessReason || "No telemetry samples found";
    }

    return "";
  };

  const fetchCurrentTelemetry = async () => {
    if (!productId) {
      return;
    }

    try {
      const cursor = telemetryCursorRef.current;
      const currentUrl = new URL(`${API_BASE}/logs/get/current/${productId}`);
      currentUrl.searchParams.set("windowSeconds", CURRENT_WINDOW_SECONDS);
      currentUrl.searchParams.set("maxRows", 120);

      if (cursor?.sampleId) {
        currentUrl.searchParams.set("afterSampleId", cursor.sampleId);
        currentUrl.searchParams.set("fallback", "false");
      }

      const response = await fetch(currentUrl.toString());
      const body = await response.json();
      if (!response.ok || body?.status !== "ok") {
        throw new Error(body?.error || `HTTP ${response.status}`);
      }

      const currentState = body.data || {};
      const newSamples = Array.isArray(currentState.recentSamples)
        ? currentState.recentSamples
        : [];

      setTelemetryFreshness(currentState);
      setFetchStatus(buildTelemetryStatusText(currentState));

      if (!cursor?.sampleId && currentState.latestSample) {
        const initialSamples = newSamples.length ? newSamples : [currentState.latestSample];
        initialSamples.forEach((sample, index) => applyTelemetrySample(sample, index > 0));
        setLogId(
          currentState.latestSample.log_id ||
            currentState.latestSample.cursor?.logId ||
            0
        );
        setProgress(100);
        return;
      }

      if (newSamples.length > 0) {
        const latestSample = newSamples[newSamples.length - 1];
        updateTelemetryCursor(latestSample);
        setLogId(latestSample.log_id || latestSample.cursor?.logId || 0);

        setLogData((prevLogData) => {
          const updatedLogData = [...prevLogData, ...newSamples];
          return updatedLogData.slice(-DATA_BUFFER_SIZE);
        });

        logDataRef.current = [...logDataRef.current, ...newSamples];
        if (logDataRef.current.length > DATA_BUFFER_SIZE) {
          logDataRef.current = logDataRef.current.slice(-DATA_BUFFER_SIZE);
        }

        maybeFastForwardQueue();
        updateProgressFromBacklog();
        return;
      }

      updateProgressFromBacklog();
    } catch (error) {
      console.error("Error fetching data:", error);
      setFetchStatus("Error fetching data");
      updateProgressFromBacklog();
    }
  };

  // Background data fetch loop
  useEffect(() => {
    if (!productId) {
      return undefined;
    }

    fetchCurrentTelemetry();

    intervalRef.current = setInterval(() => {
      fetchCurrentTelemetry();
    }, FETCH_INTERVAL);

    return () => clearInterval(intervalRef.current);
  }, [productId]);

  // Foreground loop for updating the dashboard every second with adaptive draining
  useEffect(() => {
    const updateInterval = setInterval(() => {
      if (!logDataRef.current.length) {
        updateProgressFromBacklog();
        return;
      }

      const backlogSamples = logDataRef.current.length;
      const needsCatchUp = backlogSamples > TARGET_BUFFER_SAMPLES;
      const dequeueTarget = needsCatchUp
        ? Math.min(
            MAX_DEQUEUE_PER_TICK,
            Math.max(2, Math.ceil(backlogSamples / TARGET_BUFFER_SAMPLES))
          )
        : 1;

      for (let i = 0; i < dequeueTarget; i += 1) {
        if (!logDataRef.current.length) {
          break;
        }
        processNextDataPoint();
      }
    }, 1000); // Update every second

    return () => clearInterval(updateInterval);
  }, []);

  const getValueClass = (key) =>
    highlightedFields[key] ? "dashboardDataUpdated" : "dashboardDataUnchanged";

  const getEventDisplay = () => {
    if (lastEvent.message === "No Events") return lastEvent.message;
    const elapsedTime = Math.round((new Date() - lastEvent.timestamp) / 1000);
    return `${lastEvent.message} (Occurred ${elapsedTime}s ago)`;
  };

  const firmwareVersionLabel = firmwareVersion
    ? `v${firmwareVersion}`
    : isFirmwareVersionLoading
    ? "Loading..."
    : "Unavailable";

  const healthSeverity = healthStatus?.severity || "gray";

  const healthStateMap = {
    green: {
      icon: faCircleCheck,
    },
    yellow: {
      icon: faTriangleExclamation,
    },
    red: {
      icon: faTriangleExclamation,
    },
    gray: {
      icon: faCircleQuestion,
    },
  };

  const healthStatusLabels = {
    healthy: "Healthy",
    ok: "Healthy",
    degraded: "Degraded",
    modbus_degraded: "Degraded",
    modbus_failed: "Modbus Fault",
    offline: "Offline",
    health_unavailable: "Health Unavailable",
    not_communicating: "Gateway Offline",
    unknown: "Unknown",
  };

  const getHealthPresentationReason = () => {
    const rawReason = healthStatus?.reason || "No health data";
    const currentOnlyReason = String(rawReason)
      .replace(/;?\s*upload recovered after .*?offline/gi, "")
      .trim();

    if (currentOnlyReason) {
      return currentOnlyReason;
    }

    if (healthStatus?.currentStatus === "healthy" || healthStatus?.currentStatus === "ok") {
      return "Device reporting normally";
    }

    return "No health data";
  };

  const getHealthDisplay = () => {
    if (isHealthLoading && !healthStatus) {
      return {
        icon: faHeartPulse,
        label: "Loading",
        reason: "Checking latest health",
        severity: "gray",
      };
    }

    if (healthError) {
      return {
        icon: faCircleQuestion,
        label: "Unavailable",
        reason: healthError,
        severity: "gray",
      };
    }

    const mapped = healthStateMap[healthSeverity] || healthStateMap.gray;
    return {
      ...mapped,
      label:
        healthStatusLabels[healthStatus?.currentStatus] ||
        healthStatusLabels[healthStatus?.status] ||
        "Unknown",
      reason: getHealthPresentationReason(),
      severity: healthSeverity,
    };
  };

  const formatHealthTimestamp = (value) => {
    if (!value) {
      return "N/A";
    }

    const date = new Date(value);
    return formatLocalDateTime(date);
  };

  const formatRelativeAge = (seconds) => {
    const numericSeconds = Number(seconds);
    if (!Number.isFinite(numericSeconds)) {
      return "N/A";
    }

    if (numericSeconds < 60) {
      return `${Math.max(0, Math.round(numericSeconds))}s ago`;
    }

    const minutes = Math.floor(numericSeconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours}h ${remainingMinutes}m ago` : `${hours}h ago`;
  };

  const formatDuration = (seconds) => {
    const numericSeconds = Number(seconds);
    if (!Number.isFinite(numericSeconds) || numericSeconds <= 0) {
      return "0s";
    }

    const hours = Math.floor(numericSeconds / 3600);
    const minutes = Math.floor((numericSeconds % 3600) / 60);
    const remainingSeconds = Math.round(numericSeconds % 60);

    if (hours > 0) {
      return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
    }

    if (minutes > 0) {
      return remainingSeconds ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }

    return `${remainingSeconds}s`;
  };

  const formatStatusText = (value) => {
    if (!value) {
      return "N/A";
    }

    return String(value)
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const formatUploadStateText = (value) => {
    if (String(value || "").toLowerCase() === "recovered") {
      return "Online";
    }

    return formatStatusText(value);
  };

  const formatLastModbusState = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();

    if (normalizedStatus === "ok" || normalizedStatus === "healthy") {
      return "OK";
    }

    if (normalizedStatus === "modbus_failed") {
      return "Failed";
    }

    if (normalizedStatus === "modbus_degraded" || normalizedStatus === "degraded") {
      return "Degraded";
    }

    if (
      normalizedStatus === "health_unavailable" ||
      normalizedStatus === "not_communicating" ||
      normalizedStatus === "unknown" ||
      normalizedStatus === "offline"
    ) {
      return "N/A";
    }

    return formatStatusText(status);
  };

  const parseUtcDbDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) {
      return null;
    }

    const normalizedDate = String(dateStr).trim().replace(/-/g, "/");
    const [year, month, day] = normalizedDate.split("/").map((part) => Number(part));

    const rawTime = String(timeStr).trim().split(".")[0];
    const [hoursRaw, minutesRaw, secondsRaw] = rawTime.split(":");
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    const seconds = secondsRaw == null ? 0 : Number(secondsRaw);

    if (
      [year, month, day, hours, minutes, seconds].some((value) => Number.isNaN(value))
    ) {
      return null;
    }

    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  };

  const formatLocalDateTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return "N/A";
    }

    const pad = (value) => String(value).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const getLastUpdateText = () => {
    if (dashboardData.date && dashboardData.time) {
      const sampleDate = parseUtcDbDateTime(dashboardData.date, dashboardData.time);
      if (!sampleDate) {
        return "N/A";
      }
      const elapsedMillis = Math.max(0, nowTs - sampleDate.getTime());
      const elapsedSeconds = Math.round(elapsedMillis / 1000);
      const elapsedMinutes = Math.floor(elapsedSeconds / 60);
      const minuteDisplay = `approx ${elapsedMinutes + 1}min ago`;
      return `${formatLocalDateTime(sampleDate)} (${minuteDisplay})`;
    }
    return "N/A";
  };

  useEffect(() => {
    return () => {
      Object.values(highlightTimeouts.current).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const Sparkline = ({ seriesKey, color }) => {
    const values = sparklineData[seriesKey] || [];
    const scale = sparklineScales[seriesKey] || createDefaultScale();
    return (
      <div className="sparklineWrapper">
        <Sparklines data={values} width={120} height={30} min={scale.min} max={scale.max}>
          <SparklinesLine color={color} style={{ strokeWidth: 1 }} />
        </Sparklines>
      </div>
    );
  };

  if (!product) {
    return (
      <div className="dashboardHomeContainer dashboardHomeContainer--empty">
        <Typography variant="h6">Select a product to view its dashboard.</Typography>
        <button className="navigationButton" type="button" onClick={handleNavigateHome}>
          <FontAwesomeIcon icon={faHouse} />
          My Products
        </button>
      </div>
    );
  }

  const healthDisplay = getHealthDisplay();
  const healthTooltipRows =
    healthStatus?.currentStatus === "not_communicating"
      ? [
          {
            label: "Gateway uploads",
            value: "None found",
          },
          {
            label: "Next step",
            value: "Check EVO-NetLink connectivity",
          },
        ]
      : healthStatus?.healthAvailable === false
      ? [
          {
            label: "Health telemetry",
            value: "Not available",
          },
        ]
      : [
          {
            label: "Last report",
            value: healthStatus?.uploadTimestamp
              ? formatRelativeAge(healthStatus.ageSeconds)
              : "N/A",
          },
          {
            label: "Report time",
            value: formatHealthTimestamp(healthStatus?.uploadTimestamp),
          },
          {
            label: "Last Modbus",
            value: formatLastModbusState(
              healthStatus?.reportedStatus || healthStatus?.status || healthStatus?.currentStatus
            ),
          },
          {
            label: "Upload",
            value: formatUploadStateText(healthStatus?.uploadState),
          },
        ];

  if (
    healthStatus?.healthAvailable !== false &&
    healthStatus?.uploadState === "upload_failed" &&
    Number(healthStatus?.uploadOfflineSeconds) > 0
  ) {
    healthTooltipRows.push({
      label: "Outage",
      value: formatDuration(healthStatus.uploadOfflineSeconds),
    });
  }

  const healthDetails = (
    <div className="dashboardHealthTooltipContent">
      {healthTooltipRows.map((row) => (
        <div className="dashboardHealthTooltipRow" key={row.label}>
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </div>
  );

  return (
    <div className="dashboardHomeContainer">
      <div className="dashboardHero">
        <div className="dashboardHomeHeader">
          <div className="dashboardHomeHeaderTitle">
            <Typography variant="h5" component="h1" color="inherit">
              Product:{" "}
              {product.product_description.length > 32
                ? product.product_description.slice(0, 32) + "..."
                : product.product_description}
            </Typography>
            <Typography variant="body1" component="p" color="inherit">
              Model: {product.product_type}
            </Typography>
            <Typography variant="body1" component="p" color="inherit">
              Serial Number: {product.product_serial_number}
            </Typography>
            <Typography variant="body1" component="p" color="inherit">
              Firmware: {firmwareVersionLabel}
            </Typography>
          </div>

          <div className="dashboardHomeHeaderActions">
            <button className="navigationButton" type="button" onClick={handleNavigateHome}>
              <FontAwesomeIcon icon={faHouse} />
              My Products
            </button>
          </div>
        </div>

        <section className="dashboardHomeStatus">
          <Card
            component="section"
            elevation={0}
            className="dashboardTile dashboardStatusTile"
            aria-label="Current device mode"
          >
            <CardContent className="dashboardTileBody dashboardHomeMode">
              <Typography variant="h5" component="p" className="modeTitle">
                {modeValues[dashboardData.mode] || "Loading mode..."}
              </Typography>
              <Typography variant="body2" component="p" className="modeEvent" aria-live="polite">
                {getEventDisplay()}
              </Typography>
            </CardContent>
          </Card>

          <Card
            component="section"
            elevation={0}
            className={`dashboardTile dashboardStatusTile dashboardHealthTile dashboardHealthTile--${healthDisplay.severity}`}
            aria-label="Device health"
          >
            <CardContent className="dashboardTileBody dashboardHealth">
              <Tooltip
                title={healthDetails}
                arrow
                placement="left"
                enterTouchDelay={0}
                leaveTouchDelay={5000}
                slotProps={{
                  tooltip: { className: "dashboardHealthMuiTooltip" },
                  arrow: { className: "dashboardHealthMuiTooltipArrow" },
                }}
              >
                <div
                  className="dashboardHealthStatusAnchor"
                  role="button"
                  tabIndex={0}
                  aria-label={`Device health: ${healthDisplay.label}. ${healthDisplay.reason}`}
                >
                  <div className="dashboardHealthHeader">
                    <span
                      className={`dashboardHealthIcon dashboardHealthIcon--${healthDisplay.severity}`}
                      aria-hidden="true"
                    >
                      <FontAwesomeIcon icon={healthDisplay.icon} />
                    </span>
                    <div className="dashboardHealthSummary">
                      <Typography variant="h6" component="p" className="dashboardHealthTitle">
                        {healthDisplay.label}
                      </Typography>
                      <Typography variant="body2" component="p" className="dashboardHealthReason">
                        {healthDisplay.reason}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Tooltip>
            </CardContent>
          </Card>
        </section>
      </div>

      <section className="dashboardHomeContent">
        <Card component="section" elevation={0} className="dashboardTile" aria-label="AC input telemetry">
          <CardContent className="dashboardTileBody">
            <Typography variant="h6" component="h2" className="dashboardTileTitle">
              <FontAwesomeIcon icon={faPlug} /> AC Input
            </Typography>
            <div className="dashboardMetricGrid">
              <div className="metricColumn">
                <Typography variant="subtitle2" component="p" className="metricTitle">
                  Grid
                </Typography>
                <div className="metricRow">
                  <span className="metricLabel">Voltage</span>
                  <span className={`metricValue ${getValueClass("grid_volt")}`}>
                    {formatMetricValue("grid_volt", dashboardData.grid_volt)}V
                  </span>
                </div>
                <Sparkline seriesKey="gridVoltage" color="green" />
                <div className="metricRow">
                  <span className="metricLabel">Hz</span>
                  <span className={`metricValue ${getValueClass("grid_freq")}`}>
                    {formatMetricValue("grid_freq", dashboardData.grid_freq)}Hz
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Current</span>
                  <span className={`metricValue ${getValueClass("input_current")}`}>
                    {formatMetricValue("input_current", dashboardData.input_current)}A
                  </span>
                </div>
              </div>

              <div className="metricColumn">
                <Typography variant="subtitle2" component="p" className="metricTitle">
                  Generator
                </Typography>
                <div className="metricRow">
                  <span className="metricLabel">Voltage</span>
                  <span className={`metricValue ${getValueClass("gen_volt")}`}>
                    {formatMetricValue("gen_volt", dashboardData.gen_volt)}V
                  </span>
                </div>
                <Sparkline seriesKey="generatorVoltage" color="black" />
                <div className="metricRow">
                  <span className="metricLabel">Hz</span>
                  <span className={`metricValue ${getValueClass("gen_freq")}`}>
                    {formatMetricValue("gen_freq", dashboardData.gen_freq)}Hz
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Current</span>
                  <span className={`metricValue ${getValueClass("input_current")}`}>
                    {formatMetricValue("input_current", dashboardData.input_current)}A
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Power</span>
                  <span className={`metricValue ${getValueClass("input_watt")}`}>
                    {formatPowerThresholdValue("input_watt", dashboardData.input_watt, "W")}
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">VA</span>
                  <span className={`metricValue ${getValueClass("input_va")}`}>
                    {formatPowerThresholdValue("input_va", dashboardData.input_va, "VA")}
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">PF</span>
                  <span className="metricValue">
                    {formatPowerFactor(dashboardData.input_watt, dashboardData.input_va)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card component="section" elevation={0} className="dashboardTile" aria-label="AC output telemetry">
          <CardContent className="dashboardTileBody">
            <Typography variant="h6" component="h2" className="dashboardTileTitle">
              <FontAwesomeIcon icon={faBolt} /> AC Output
            </Typography>
            <div className="dashboardMetricGrid">
              <div className="metricColumn">
                <div className="metricRow">
                  <span className="metricLabel">Voltage</span>
                  <span className={`metricValue ${getValueClass("output_volt")}`}>
                    {formatMetricValue("output_volt", dashboardData.output_volt)}VAC
                  </span>
                </div>
                <Sparkline seriesKey="outputVoltage" color="blue" />
                <div className="metricRow">
                  <span className="metricLabel">Frequency</span>
                  <span className={`metricValue ${getValueClass("output_freq")}`}>
                    {formatMetricValue("output_freq", dashboardData.output_freq)}Hz
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Current</span>
                  <span className={`metricValue ${getValueClass("output_current")}`}>
                    {formatMetricValue("output_current", dashboardData.output_current)}A
                  </span>
                </div>
              </div>
              <div className="metricColumn">
                <div className="metricRow">
                  <span className="metricLabel">Power</span>
                  <span className={`metricValue ${getValueClass("output_watt")}`}>
                    {formatPowerThresholdValue("output_watt", dashboardData.output_watt, "W")}
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Apparent Power</span>
                  <span className={`metricValue ${getValueClass("output_va")}`}>
                    {formatPowerThresholdValue("output_va", dashboardData.output_va, "VA")}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card component="section" elevation={0} className="dashboardTile" aria-label="Battery status">
          <CardContent className="dashboardTileBody">
            <Typography variant="h6" component="h2" className="dashboardTileTitle">
              <FontAwesomeIcon icon={faBatteryFull} /> Battery
            </Typography>
            <div className="dashboardMetricGrid">
              <div className="metricColumn">
                <div className="metricRow">
                  <span className="metricLabel">Voltage</span>
                  <span className={`metricValue ${getValueClass("battery_volt")}`}>
                    {formatMetricValue("battery_volt", dashboardData.battery_volt)}VDC
                  </span>
                </div>
                <Sparkline seriesKey="batteryVoltage" color="red" />
                <div className="metricRow">
                  <span className="metricLabel">Amps</span>
                  <span className={`metricValue ${getValueClass("battery_current")}`}>
                    {formatMetricValue("battery_current", dashboardData.battery_current)}A
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">External</span>
                  <span className={`metricValue ${getValueClass("external_current")}`}>
                    {formatMetricValue("external_current", dashboardData.external_current)}A
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Status</span>
                  <span className={`metricValue ${getValueClass("charge_stage")}`}>
                    {formatMetricValue("charge_stage", dashboardData.charge_stage)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card component="section" elevation={0} className="dashboardTile" aria-label="Temperature sensors">
          <CardContent className="dashboardTileBody">
            <Typography variant="h6" component="h2" className="dashboardTileTitle">
              <FontAwesomeIcon icon={faThermometerHalf} /> Temperature
            </Typography>
            <div className="dashboardMetricGrid">
              <div className="metricColumn">
                <div className="metricRow">
                  <span className="metricLabel">Battery</span>
                  <span className={`metricValue ${getValueClass("battery_temp")}`}>
                    {formatMetricValue("battery_temp", dashboardData.battery_temp)}°C
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Heat Sink</span>
                  <span className={`metricValue ${getValueClass("heatsink_temp")}`}>
                    {formatMetricValue("heatsink_temp", dashboardData.heatsink_temp)}°C
                  </span>
                </div>
                <div className="metricRow">
                  <span className="metricLabel">Transformer</span>
                  <span className={`metricValue ${getValueClass("transformer_temp")}`}>
                    {formatMetricValue("transformer_temp", dashboardData.transformer_temp)}°C
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <div className="dashboardHomeFooter">
        <Typography variant="body2" component="p">
          {fetchStatus || "\u00A0"}
        </Typography>
        <Typography variant="body2" component="p">
          Last update: {getLastUpdateText()}
        </Typography>
      </div>
      <LinearProgress variant="determinate" value={progress} aria-label="Log replay progress" />
    </div>
  );
};

export default DashboardHome;
